import { useState } from "react";
import type { Route, Step } from "~/api/types";
import { formatUsd, formatTokenAmount } from "~/lib/format";
import { getRouteScoreUsd } from "~/lib/routeMetrics";
import { useChains } from "~/hooks/useChains";

export function RouteCard({
  route,
  isSelected,
  isFirst,
  bestAmount,
  bestRouteUsd,
  onClick,
}: {
  route: Route;
  isSelected: boolean;
  isFirst: boolean;
  bestAmount: bigint;
  bestRouteUsd: number | null;
  onClick: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { summary, steps } = route;

  const gasFee = summary.totalFees.find((f) => f.type === "GAS_ESTIMATE");
  const routeScoreUsd = getRouteScoreUsd(summary);
  const toDecimals = steps.length > 0 ? getOutputDecimals(steps) : 18;

  const lastStep = steps[steps.length - 1];
  const toSymbol =
    lastStep && "to" in lastStep && lastStep.to?.symbol
      ? lastStep.to.symbol
      : "";

  let thisAmount = 0n;
  try { thisAmount = BigInt(summary.toAmountEstimated); } catch { /* malformed */ }
  const lossBps = !isFirst
    ? routeScoreUsd != null && bestRouteUsd != null && bestRouteUsd > 0
      ? Math.max(0, Math.round((1 - routeScoreUsd / bestRouteUsd) * 10000))
      : bestAmount > 0n
        ? Number(((bestAmount - thisAmount) * 10000n) / bestAmount)
        : 0
    : 0;

  // Build executor chain from actionable steps (swap/bridge)
  const executors = getExecutorChain(steps);
  const hasSteps = steps.length > 0;

  return (
    <div
      onClick={onClick}
      style={{
        animation:
          "swing-in-left-fwd 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both",
      }}
      className={`mt-3 cursor-pointer rounded-[var(--radius-lg)] p-3 transition-colors ${
        isSelected
          ? "border border-[var(--color-border-route-selected)] bg-[var(--color-bg-route-selected)]"
          : "border border-[var(--color-border-card-section)] bg-[var(--color-bg-card-section)] hover:bg-[var(--color-bg-route-hover)]"
      }`}
    >
      {/* Row 1: token amount + symbol | time | badge + chevron */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-[18px] font-bold leading-normal text-[var(--color-text-primary)]">
            {formatTokenAmount(summary.toAmountEstimated, toDecimals)}
            {toSymbol && (
              <span className="ml-1 font-semibold text-[var(--color-text-secondary)]">
                {toSymbol}
              </span>
            )}
          </span>

          {/* Estimated time */}
          {summary.estimatedDurationSec != null && (
            <span className="flex items-center gap-1 pt-[2px] text-[13px] text-[var(--color-text-tertiary)]">
              <span>in</span>
              <ClockIcon />
              <span className="text-[var(--color-text-secondary)]">
                {formatDurationShort(summary.estimatedDurationSec)}
              </span>
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {/* Best / loss badge */}
          {isFirst && (
            <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold leading-tight text-[var(--color-success)]" style={{ background: "rgba(5, 150, 105, 0.2)" }}>
              Best
            </span>
          )}

          {/* Chevron toggle (only if there are steps) */}
          {hasSteps && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-tertiary)]"
            >
              <ChevronIcon expanded={expanded} />
            </button>
          )}
        </div>
      </div>

      {/* Expanded steps section */}
      {expanded && hasSteps && (
        <div className="mt-2 flex flex-col gap-3 border-t border-[var(--color-border-card-section)] pt-3">
          <div className="flex flex-col">
            {steps
              .filter(
                (s): s is Extract<Step, { type: "SWAP" | "BRIDGE" }> =>
                  s.type === "SWAP" || s.type === "BRIDGE",
              )
              .map((step, i, arr) => {
                const isLast = i === arr.length - 1;
                return (
                  <div
                    key={step.stepId}
                    className={`relative ${!isLast ? "pb-3" : ""}`}
                  >
                    {/* Vertical connector line */}
                    {!isLast && (
                      <div className="absolute left-[9.5px] top-[16px] h-[calc(100%-7px)] w-px bg-[var(--color-border-card-section)]" />
                    )}
                    <div className="relative z-10">
                      <StepRow step={step} />
                    </div>
                  </div>
                );
              })}
          </div>

          {routeScoreUsd != null && (
            <div className="flex items-end justify-between">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-[var(--color-text-tertiary)]">
                  Net Output
                </span>
                <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {formatUsd(String(routeScoreUsd))}
                </span>
              </div>
            </div>
          )}

          {/* Slippage + minimum out */}
          {(summary.priceImpactBps != null ||
            summary.toAmountMin !== "0") && (
            <div className="flex items-end justify-between">
              {summary.priceImpactBps != null && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-[var(--color-text-tertiary)]">
                    Effective Slippage
                  </span>
                  <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                    {(summary.priceImpactBps / 100).toFixed(2)}%
                  </span>
                </div>
              )}
              {summary.toAmountMin !== "0" && (
                <div className="flex flex-col items-end gap-0.5">
                  <span className="text-xs font-medium text-[var(--color-text-tertiary)]">
                    Minimum Out
                  </span>
                  <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                    {formatTokenAmount(summary.toAmountMin, toDecimals)}{" "}
                    {toSymbol}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Row 2 (bottom): executor · via aggregator | gas + loss% */}
      <div className="mt-1.5 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {/* Show executor chain, skip if redundant with aggregator */}
          {executors &&
            !namesOverlap(executors, summary.provider.displayName ?? summary.provider.name) && (
              <>
                <span className="text-[13px] text-[var(--color-text-tertiary)]">
                  {executors}
                </span>
                <span className="text-[13px] text-[var(--color-text-tertiary)]">
                  ·
                </span>
              </>
            )}
          <span className="text-[13px] text-[var(--color-text-tertiary)]">
            via
          </span>
          {summary.provider.logoUrl && (
            <img
              src={summary.provider.logoUrl}
              alt=""
              className="h-4 w-4 rounded-sm"
            />
          )}
          <span className="text-[13px] font-medium text-[var(--color-text-secondary)]">
            {summary.provider.displayName ?? summary.provider.name}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {gasFee?.amountUsd && (
            <span className="flex items-center gap-0.5 text-[13px] text-[var(--color-text-tertiary)]">
              <GasIcon />
              <span className="font-medium">
                {formatUsd(gasFee.amountUsd)}
              </span>
            </span>
          )}
          {routeScoreUsd != null && (
            <span className="text-[13px] text-[var(--color-text-tertiary)]">
              Net{" "}
              <span className="font-medium text-[var(--color-text-secondary)]">
                {formatUsd(String(routeScoreUsd))}
              </span>
            </span>
          )}
          {!isFirst && lossBps > 0 && (
            <span
              className={`text-[12px] font-medium ${
                lossBps > 500
                  ? "text-[var(--color-danger)]"
                  : lossBps > 100
                    ? "text-[var(--color-warning)]"
                    : "text-[var(--color-text-tertiary)]"
              }`}
            >
              -{(lossBps / 100).toFixed(2)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Step Row (expanded view) ─── */

function StepRow({
  step,
}: {
  step: Extract<Step, { type: "SWAP" | "BRIDGE" }>;
}) {
  const { getChain } = useChains();
  const isBridge = step.type === "BRIDGE";
  const isCrossChain =
    isBridge && "fromChainId" in step && "toChainId" in step && step.fromChainId !== step.toChainId;

  let label: string;
  if (isBridge && "fromChainId" in step && "toChainId" in step && isCrossChain) {
    const fromName =
      getChain(String(step.fromChainId))?.name ?? step.fromChainId;
    const toName =
      getChain(String(step.toChainId))?.name ?? step.toChainId;
    label = `Bridge from ${fromName} to ${toName} via ${step.provider}`;
  } else {
    const chainName =
      "chainId" in step
        ? getChain(String(step.chainId))?.name ?? ""
        : "";
    label = chainName
      ? `Swap on ${chainName} via ${step.provider}`
      : `Swap via ${step.provider}`;
  }

  const fromSymbol = step.from?.symbol ?? "?";
  const toSymbol = step.to?.symbol ?? "?";
  const fromAmount =
    step.amounts?.fromAmount && step.from?.decimals != null
      ? formatTokenAmount(step.amounts.fromAmount, step.from.decimals)
      : "";
  const toAmount =
    step.amounts?.toAmountEstimated && step.to?.decimals != null
      ? formatTokenAmount(step.amounts.toAmountEstimated, step.to.decimals)
      : "";

  return (
    <div className="flex items-start gap-2">
      {/* Step icon circle */}
      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-secondary)]">
        {isCrossChain ? (
          <BridgeStepIcon />
        ) : (
          <SwapStepIcon />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-0.5">
        <span className="text-[12px] font-medium text-[var(--color-text-tertiary)]">
          {label}
        </span>
        <div className="flex items-center gap-1 text-[13px] font-medium text-[var(--color-text-primary)]">
          <span>
            {fromAmount} {fromSymbol}
          </span>
          <ArrowRightIcon />
          <span>
            {toAmount} {toSymbol}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Helpers ─── */

function getOutputDecimals(steps: Route["steps"]): number {
  const last = steps[steps.length - 1];
  if ("to" in last && last.to?.decimals != null) return last.to.decimals;
  return 18;
}

/** "mayan swift" → "Mayan Swift" */
function titleCase(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Build "Mayan → Stargate" executor chain from actionable steps */
function getExecutorChain(steps: Route["steps"]): string {
  const providers = steps
    .filter((s) => s.type === "SWAP" || s.type === "BRIDGE")
    .map((s) => ("provider" in s ? titleCase(s.provider) : ""))
    .filter(Boolean);
  if (providers.length === 0) return "";
  const deduped = providers.filter((p, i) => i === 0 || p !== providers[i - 1]);
  return deduped.join(" → ");
}

/** Check if executor and aggregator names are redundant (equal or one contains the other) */
function namesOverlap(a: string, b: string): boolean {
  const al = a.toLowerCase().replace(/[\s\-_]/g, "");
  const bl = b.toLowerCase().replace(/[\s\-_]/g, "");
  return al === bl || al.includes(bl) || bl.includes(al);
}

/** Compact duration: "2s", "8m", "1h" — no tilde */
function formatDurationShort(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

/* ─── Icons ─── */

function ClockIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[var(--color-text-tertiary)]"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function GasIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="text-[var(--color-text-tertiary)]"
    >
      <path
        fillRule="evenodd"
        d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`text-[var(--color-text-tertiary)] transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="shrink-0 text-[var(--color-text-tertiary)]"
    >
      <path
        fillRule="evenodd"
        d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SwapStepIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[var(--color-text-secondary)]"
    >
      <path d="M16 3l4 4-4 4" />
      <path d="M20 7H4" />
      <path d="M8 21l-4-4 4-4" />
      <path d="M4 17h16" />
    </svg>
  );
}

function BridgeStepIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[var(--color-text-secondary)]"
    >
      <path d="M4 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
      <path d="M20 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
      <path d="M6 8h12" />
      <path d="M6 12v6" />
      <path d="M18 12v6" />
      <path d="M3 18h4" />
      <path d="M17 18h4" />
    </svg>
  );
}
